import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  Autocomplete,
  ModalOpenProcess,
  ModalOpenProcessRef,
  ellipsisStyle,
} from "@msys/ui";
import type { AutocompleteCloseReason } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { Box, IconButton, Stack, StackProps, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { debounce } from "lodash-es";
import React from "react";
import { ContactLine, LocationLine } from "../../commons/DataItem.js";
import { highlightWithBold } from "../../utils.js";
import { getAddressLabel } from "../addresses/helpers.js";
import { CrmCreateModal } from "../crm/CrmCreateModal.js";
import { CrmContactType } from "../crm/useCrmContactTypes.js";
import { CrmTagChips } from "../crm/CrmTagChips.js";
import { OrganisationAvatar } from "../organisations/OrganisationAvatar.js";
import { OrganisationCategoryAvatar } from "../organisations/OrganisationCategoryAvatar.js";
import {
  CrmCompanySelect_CrmCompaniesRecordFragment,
  useCrmCompanySelectLazyQuery,
} from "./CrmCompanySelect.generated.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { EntityNumber } from "../../commons/EntityNumber.js";
import { CustomFieldFilter } from "../../../clients/graphqlTypes.js";

const DEFAULT_ALLOWED_CONTACT_TYPES: CrmContactType[] = [
  "INDIVIDUAL",
  "COMPANY",
];

export type CrmCompanySelectEntry = Omit<
  CrmCompanySelect_CrmCompaniesRecordFragment,
  "systemTags" | "customTags" | "__typename"
>;

interface Props {
  crmCompanyId?: string | undefined | null;
  onChange: (crmCompany: CrmCompanySelectEntry | null) => void;
  filterProjectId?: string | undefined;
  filterTagsAll?: string[] | null | undefined;
  filterTagsAny?: string[] | null | undefined;
  filterTagsExclude?: string[] | null | undefined;
  filterBusinessPartnerRolesAll?: string[] | null | undefined;
  filterBusinessPartnerRolesExclude?: string[] | null | undefined;
  filterCustomFields?: CustomFieldFilter[] | undefined;
  inputLabel: string;
  placeholder?: string;
  required?: boolean;
  canCreateNew?: boolean;
  createModalTitle?: string;
  createNewLabel?: string;
  allowedContactTypesForCreate?: CrmContactType[];
  createModalPrefill?: React.ComponentProps<typeof CrmCreateModal>["prefill"];
  error?: string;
  disabled?: boolean;
  disableBlockedBusinessPartners?: boolean;
  // Opens the option list as soon as the input is focused.
  openOnFocus?: boolean;
  // The option list closed (blur, Escape, an option was chosen, …).
  onClose?: (reason: AutocompleteCloseReason) => void;
  // The create-contact form closed, whether completed or cancelled.
  onCreateModalClose?: () => void;
  // The "+" button next to the input; a caller may render its own instead.
  showCreateButton?: boolean;
  // The text in the search input changed (typed, cleared, or replaced by the
  // chosen contact's title). Lets a caller's own create path start from the
  // typed text the way Enter does.
  onInputValueChange?: (value: string) => void;
}

export const CrmCompanySelect = ({
  crmCompanyId,
  onChange,
  filterProjectId,
  filterTagsAll,
  filterTagsAny,
  filterTagsExclude,
  filterBusinessPartnerRolesAll,
  filterBusinessPartnerRolesExclude,
  filterCustomFields,
  inputLabel,
  placeholder,
  required = false,
  canCreateNew = false,
  createModalTitle,
  allowedContactTypesForCreate = DEFAULT_ALLOWED_CONTACT_TYPES,
  createNewLabel,
  createModalPrefill,
  error,
  disabled,
  disableBlockedBusinessPartners = true,
  openOnFocus,
  onClose,
  onCreateModalClose,
  showCreateButton = true,
  onInputValueChange,
  ...stackProps
}: Props & Pick<StackProps, "sx">) => {
  const { t } = useTranslate(["Global"]);

  const [inputValue, setInputValue] = React.useState("");
  const onInputValueChangeRef = React.useRef(onInputValueChange);
  onInputValueChangeRef.current = onInputValueChange;
  React.useEffect(() => {
    onInputValueChangeRef.current?.(inputValue);
  }, [inputValue]);

  // Whether Enter would select an option: only then is it left to the
  // Autocomplete. Otherwise Enter on typed text starts a new contact with the
  // text as first name (see `handleKeyDown`), instead of falling through to
  // the surrounding form.
  const highlightedRef = React.useRef<unknown>(null);
  const popupOpenRef = React.useRef(false);

  const { highlightText, crmCompanies, debouncedRefetch, loading, totalCount } =
    useCrmCompanies(
      crmCompanyId,
      inputValue,
      setInputValue,
      {
        filterProjectId,
        filterTagsAll,
        filterTagsAny,
        filterBusinessPartnerRolesAll,
        filterBusinessPartnerRolesExclude,
        filterTagsExclude,
        filterCustomFields,
      },
      disableBlockedBusinessPartners
    );

  const processRef = React.useRef<
    ModalOpenProcessRef<
      Omit<React.ComponentProps<typeof CrmCreateModal>, "handleClose"> & {
        handleClose?(): void;
      }
    >
  >();

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement> & {
      defaultMuiPrevented?: boolean;
    }
  ) => {
    if (event.key !== "Enter" || !canCreateNew) return;
    if (popupOpenRef.current && highlightedRef.current !== null) return;
    const firstname = inputValue.trim();
    if (!firstname) return;
    event.preventDefault();
    event.defaultMuiPrevented = true;
    processRef.current?.open({ prefill: { firstname } });
  };

  return (
    <>
      <Stack direction="row" spacing={1} {...stackProps}>
        <Autocomplete
          placeholder={placeholder ?? t("Type to search", { ns: "Global" })}
          options={[
            ...crmCompanies,
            ...(canCreateNew && createNewLabel && !crmCompanyId
              ? [
                  {
                    title: createNewLabel,
                    id: "__create_new__" as const,
                    disabled: false,
                  },
                ]
              : []),
            ...(totalCount > crmCompanies.length
              ? [
                  {
                    title: t("Type to see more...", { ns: "Global" }),
                    id: "__type_to_see_more__" as const,
                    disabled: true,
                  },
                ]
              : []),
          ]}
          getOptionDisabled={option => option.disabled}
          getOptionLabel={option => option.title}
          renderOption={(props, option) =>
            "__typename" in option ? (
              <CrmCompanyListItem
                {...props}
                key={option.id}
                option={option}
                highlightText={highlightText}
                showAvatar
              />
            ) : option.id === "__create_new__" ? (
              <li
                {...props}
                key={option.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <AddIcon sx={{ mr: 0.5 }} color="secondary" />
                <Typography
                  color="textSecondary"
                  sx={{
                    fontWeight: "500",
                  }}
                >
                  {option.title}
                </Typography>
              </li>
            ) : (
              <li {...props} key={option.id}>
                {option.title}
              </li>
            )
          }
          inputLabel={inputLabel}
          inputValue={inputValue}
          onInputChange={(_, value, reason) => {
            if (reason === "input") {
              setInputValue(value);
              debouncedRefetch(value);
              if (crmCompanyId) onChange(null);
            }
          }}
          required={required}
          value={crmCompanies.find(c => c.id === crmCompanyId) ?? null}
          onChange={value => {
            if (value && "__typename" in value) {
              setInputValue(value?.title ?? "");
              onChange(value);
            } else if (value && value.id === "__create_new__") {
              processRef.current?.open();
            } else if (!value) {
              setInputValue("");
              onChange(null);
            }
          }}
          loading={loading}
          error={error}
          disabled={disabled}
          openOnFocus={openOnFocus}
          onOpen={() => {
            popupOpenRef.current = true;
          }}
          onClose={(_, reason) => {
            popupOpenRef.current = false;
            onClose?.(reason);
          }}
          onHighlightChange={(_, option) => {
            highlightedRef.current = option;
          }}
          onKeyDown={handleKeyDown}
        />
        {canCreateNew && showCreateButton && !disabled && (
          <RestrictedByOrganisationPermissionWithDebug permission="crm_record_update">
            <IconButton
              color="primary"
              size="large"
              onClick={() => processRef.current?.open()}
              sx={{ alignSelf: "center" }}
            >
              <AddIcon />
            </IconButton>
          </RestrictedByOrganisationPermissionWithDebug>
        )}
      </Stack>
      <RestrictedByOrganisationPermissionWithDebug permission="crm_record_update">
        <ModalOpenProcess
          ref={processRef}
          Modal={CrmCreateModal}
          modalProps={{
            id: "create-crm-modal",
            title: createModalTitle,
            allowedContactTypes: allowedContactTypesForCreate,
            prefill: createModalPrefill,
            handleClose: onCreateModalClose,
            handleComplete: async (handleClose, organisation) => {
              setInputValue(organisation.title);
              onChange(organisation);
              handleClose();
            },
          }}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    </>
  );
};

export function CrmCompanyListItem({
  option,
  highlightText,
  showAvatar = false,
  ...props
}: React.HTMLAttributes<HTMLLIElement> & {
  option: CrmCompanySelect_CrmCompaniesRecordFragment;
  highlightText: (text: string | null) => React.ReactNode;
  showAvatar?: boolean;
}) {
  return (
    <li {...props} key={option.id}>
      {showAvatar && (
        <OrganisationAvatar
          size="s"
          organisationAvatar={option}
          style={{ marginRight: "12px" }}
        />
      )}
      <Stack
        direction="column"
        spacing={0.5}
        sx={{
          minWidth: 0,
          flex: 1,
        }}
      >
        <div>
          {highlightText(option.title)}
          {option.abcCategory && option.abcCategory !== "none" && (
            <span
              style={{
                display: "inline-flex",
                marginLeft: "8px",
                verticalAlign: "middle",
                height: "20px",
                marginTop: "-8px",
              }}
            >
              <OrganisationCategoryAvatar abcCategory={option.abcCategory} />
            </span>
          )}
        </div>
        <Typography
          variant="caption"
          component="div"
          sx={{
            color: "grey.600",
            ...ellipsisStyle,
            alignSelf: "flex-start",
            mt: "2px !important",
          }}
        >
          <EntityNumber number={option.number} />
        </Typography>
        {option.contactPerson && (
          <div style={{ ...ellipsisStyle, alignSelf: "flex-start" }}>
            <ContactLine>
              {highlightText(option.contactPerson.fullname)}
            </ContactLine>
          </div>
        )}
        {option.billingAddress && (
          <div style={{ ...ellipsisStyle, alignSelf: "flex-start" }}>
            <LocationLine>
              {highlightText(getAddressLabel(option.billingAddress))}
            </LocationLine>
          </div>
        )}
      </Stack>
      {option.systemTags.length > 0 || option.customTags.length > 0 ? (
        <Box
          sx={{
            maxWidth: "50%",
            ml: 0.5,
          }}
        >
          <CrmTagChips
            tags={[...option.systemTags, ...option.customTags]}
            boxProps={{ sx: { justifyContent: "flex-end" } }}
          />
        </Box>
      ) : null}
    </li>
  );
}

const LIMIT = 10;

function useCrmCompanies(
  crmCompanyId: string | undefined | null,
  inputValue: string,
  setInputValue: (value: string) => void,
  filters: {
    filterProjectId: string | undefined;
    filterTagsAll: string[] | null | undefined;
    filterTagsAny: string[] | null | undefined;
    filterTagsExclude: string[] | null | undefined;
    filterBusinessPartnerRolesAll: string[] | null | undefined;
    filterBusinessPartnerRolesExclude: string[] | null | undefined;
    filterCustomFields: CustomFieldFilter[] | undefined;
  },
  disableBlockedBusinessPartners: boolean
) {
  const client = useApolloClient();
  const [fetchQuery, query] = useCrmCompanySelectLazyQuery({
    client,
    variables: {
      limit: LIMIT,
      filterProjectIds: filters.filterProjectId
        ? [filters.filterProjectId]
        : null,
      filterTagsAll: filters.filterTagsAll,
      filterTagsAny: filters.filterTagsAny,
      filterTagsExclude: filters.filterTagsExclude,
      filterBusinessPartnerRolesAll: filters.filterBusinessPartnerRolesAll,
      filterBusinessPartnerRolesExclude:
        filters.filterBusinessPartnerRolesExclude,
      filterCustomFields: filters.filterCustomFields,
    },
  });

  const inputValueRef = React.useRef(inputValue);
  inputValueRef.current = inputValue;

  const refetch = React.useCallback(
    async (value: string) => {
      await fetchQuery({
        variables: {
          limit: LIMIT,
          filterProjectIds: filters.filterProjectId
            ? [filters.filterProjectId]
            : null,
          filterTagsAll: filters.filterTagsAll,
          filterTagsAny: filters.filterTagsAny,
          filterBusinessPartnerRolesAll: filters.filterBusinessPartnerRolesAll,
          searchTerm: value,
          filterIds: null,
        },
      });
    },
    [
      filters.filterProjectId,
      filters.filterTagsAll,
      filters.filterTagsAny,
      filters.filterBusinessPartnerRolesAll,
      fetchQuery,
    ]
  );

  const debouncedRefetch = React.useMemo(
    () => debounce(refetch, 500),
    [refetch]
  );

  React.useEffect(() => {
    (async () => {
      if (crmCompanyId) {
        const result = await fetchQuery({
          variables: {
            limit: 1,
            // scope out any filters to get the crmCompany under all circumstances
            // TODO: is this correct? Should we just call the crmCompany endpoint instead?
            filterBusinessPartnerRolesAll: null,
            filterExcludeIds: null,
            filterProjectIds: null,
            filterTagsAll: null,
            filterTagsAny: null,
            searchTerm: null,
            filterIds: [crmCompanyId],
          },
        });
        setInputValue(
          getDataOrNull(result.data?.crmCompanies)?.edges[0].node.title ?? ""
        );
      } else {
        await fetchQuery({
          variables: {
            limit: LIMIT,
            filterProjectIds: filters.filterProjectId
              ? [filters.filterProjectId]
              : null,
            filterTagsAll: filters.filterTagsAll,
            filterTagsAny: filters.filterTagsAny,
            filterBusinessPartnerRolesAll:
              filters.filterBusinessPartnerRolesAll,
            searchTerm: inputValueRef.current,
            filterIds: null,
          },
        });
      }
    })();
  }, [
    crmCompanyId,
    filters.filterProjectId,
    filters.filterTagsAll,
    filters.filterTagsAny,
    filters.filterBusinessPartnerRolesAll,
    fetchQuery,
    setInputValue,
  ]);

  const crmCompanies =
    getDataOrNull((query.data ?? query.previousData)?.crmCompanies)?.edges.map(
      edge => ({
        ...edge.node,
        disabled:
          disableBlockedBusinessPartners &&
          (edge.node.businessPartnerStatus === "GLOBAL_BLOCK" ||
            edge.node.businessPartnerStatus === "ORDER_BLOCK"),
      })
    ) ?? [];
  const totalCount =
    getDataOrNull((query.data ?? query.previousData)?.crmCompanies)
      ?.totalCount ?? 0;

  const highlightText = React.useCallback(
    (text: string | null) =>
      text && query?.variables?.searchTerm
        ? highlightWithBold(text, query.variables.searchTerm)
        : text,
    [query?.variables?.searchTerm]
  );

  return {
    highlightText,
    crmCompanies,
    refetch,
    debouncedRefetch,
    loading:
      query.loading ||
      Boolean(
        inputValue &&
        query?.variables?.searchTerm &&
        query?.variables?.searchTerm !== inputValue
      ),
    totalCount,
  };
}
